import { useTranslation } from "react-i18next";
import { localizeBrand } from "../../utils/brandTerms";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { useCompatibility } from "./useCompatibility";
import { optionLabel } from "./labels";
import {
  CompatibilityProfile,
  COMMUNICATION_STYLES,
  FormData,
  GENDER_OPTIONS,
  INITIAL_FORM_DATA,
  INTERESTS,
  LOOKING_FOR,
  PERSONALITY_TYPES,
  VALUE_LABELS,
} from "./types";
import {
  Heart,
  Sparkles,
  Moon,
  Sun,
  Feather,
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  User,
} from "lucide-react";

const toProfilePayload = (data: FormData): Partial<CompatibilityProfile> => ({
  ...data,
  age: parseInt(data.age, 10),
});

export const CompatibilityForm: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, updateCurrentUser } = useAuth();
  const {
    loading,
    currentStep,
    setCurrentStep,
    hasProfile,
    checkUserProfile,
    saveFormData,
  } = useCompatibility(user?.hasCompatibilityProfile);

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (user?.hasCompatibilityProfile === undefined) {
      checkUserProfile().catch(() =>
        toast.error(t("social.form.loadStatusFailed")),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasProfile) {
      navigate("/matches");
    }
  }, [hasProfile, navigate]);

  const updateField = <K extends keyof FormData>(
    field: K,
    value: FormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateValue = (key: keyof FormData["values"], value: number) => {
    setFormData((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: value },
    }));
  };

  const toggleArray = (field: "lookingFor" | "interests", value: string) => {
    setFormData((prev) => {
      const current = prev[field];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const validatePage = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          toast.error(t("social.form.err.name"));
          return false;
        }
        if (!formData.age || parseInt(formData.age, 10) < 18) {
          toast.error(t("social.form.err.age"));
          return false;
        }
        if (!formData.city.trim()) {
          toast.error(t("social.form.err.city"));
          return false;
        }
        return true;

      case 2:
        if (formData.lookingFor.length === 0) {
          toast.error(t("social.form.err.lookingFor"));
          return false;
        }
        if (
          formData.lookingFor.includes("Other") &&
          !formData.lookingForOther.trim()
        ) {
          toast.error(t("social.form.err.lookingForOther"));
          return false;
        }
        return true;

      case 3:
        if (formData.interests.length === 0) {
          toast.error(t("social.form.err.interests"));
          return false;
        }
        if (
          formData.interests.includes("Other") &&
          !formData.interestsOther.trim()
        ) {
          toast.error(t("social.form.err.interestsOther"));
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (!validatePage(currentStep)) return;

    if (currentStep === 4) {
      setIsSubmitting(true);
      try {
        await saveFormData(toProfilePayload(formData), true);
        updateCurrentUser({ hasCompatibilityProfile: true });
        navigate("/matches");
      } catch (error) {
        toast.error(t("social.form.saveFailed"));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        await saveFormData(toProfilePayload(formData), false);
        setCurrentStep(currentStep + 1);
      } catch (error) {
        toast.error(t("social.form.progressFailed"));
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepIcon = (step: number) => {
    switch (step) {
      case 1:
        return <User className="w-5 h-5" />;
      case 2:
        return <Heart className="w-5 h-5" />;
      case 3:
        return <Sparkles className="w-5 h-5" />;
      case 4:
        return <Moon className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1:
        return t("social.form.step1.title");
      case 2:
        return t("social.form.step2.title");
      case 3:
        return t("social.form.step3.title");
      case 4:
        return t("social.form.step4.title");
      default:
        return "";
    }
  };

  const getStepSubtitle = (step: number) => {
    switch (step) {
      case 1:
        return t("social.form.step1.subtitle");
      case 2:
        return t("social.form.step2.subtitle");
      case 3:
        return t("social.form.step3.subtitle");
      case 4:
        return t("social.form.step4.subtitle");
      default:
        return "";
    }
  };

  const renderPage = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                {t("social.form.fullName")} <span className="text-rose-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full bg-[#221a29]/80 text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 [color-scheme:dark] rounded-2xl px-5 py-3.5 text-base border-2 border-white/10 focus:border-[#a78bff] focus:outline-none focus:ring-4 focus:ring-[#a78bff]/20 transition-all duration-200 placeholder:text-[#c8bfd1]"
                  placeholder={t("social.form.namePlaceholder")}
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                  {t("social.form.age")} <span className="text-rose-400">*</span>
                </label>
                <input
                  type="number"
                  className="w-full bg-[#221a29]/80 text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 [color-scheme:dark] rounded-2xl px-5 py-3.5 text-base border-2 border-white/10 focus:border-[#a78bff] focus:outline-none focus:ring-4 focus:ring-[#a78bff]/20 transition-all duration-200 placeholder:text-[#c8bfd1]"
                  placeholder={t("social.form.agePlaceholder")}
                  value={formData.age}
                  onChange={(e) => updateField("age", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                  {t("social.form.gender")} <span className="text-rose-400">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {GENDER_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                        formData.gender === opt
                          ? "bg-[#a78bff] text-[#0f0a14] shadow-lg shadow-black/30"
                          : "bg-[#221a29] text-[#c8bfd1] hover:bg-[#2a2133]"
                      }`}
                      onClick={() => updateField("gender", opt)}
                    >
                      {optionLabel("gender", opt)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                {t("social.form.city")} <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                className="w-full bg-[#221a29]/80 text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 [color-scheme:dark] rounded-2xl px-5 py-3.5 text-base border-2 border-white/10 focus:border-[#a78bff] focus:outline-none focus:ring-4 focus:ring-[#a78bff]/20 transition-all duration-200 placeholder:text-[#c8bfd1]"
                placeholder={t("social.form.cityPlaceholder")}
                value={formData.city}
                onChange={(e) => updateField("city", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                {t("social.form.dob")}
              </label>
              <input
                type="date"
                className="w-full bg-[#221a29]/80 text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 [color-scheme:dark] rounded-2xl px-5 py-3.5 text-base border-2 border-white/10 focus:border-[#a78bff] focus:outline-none focus:ring-4 focus:ring-[#a78bff]/20 transition-all duration-200"
                value={formData.birthDate}
                onChange={(e) => updateField("birthDate", e.target.value)}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <p className="text-[#c8bfd1] text-sm italic">
              {t("social.form.selectAll")}
            </p>
            <div className="flex flex-wrap gap-3">
              {LOOKING_FOR.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                    formData.lookingFor.includes(opt)
                      ? "bg-[#a78bff] border-transparent text-[#0f0a14] shadow-lg shadow-black/30"
                      : "bg-[#1a131f] border-white/10 text-[#c8bfd1] hover:border-[#a78bff]/50 hover:bg-white/10"
                  }`}
                  onClick={() => toggleArray("lookingFor", opt)}
                >
                  {optionLabel("lookingFor", opt)}
                </button>
              ))}
            </div>

            {formData.lookingFor.includes("Other") && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                  {t("social.form.specify")} <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full bg-[#221a29]/80 text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 [color-scheme:dark] rounded-2xl px-5 py-3.5 text-base border-2 border-white/10 focus:border-[#a78bff] focus:outline-none focus:ring-4 focus:ring-[#a78bff]/20 transition-all duration-200"
                  placeholder={t("social.form.lookingForOtherPlaceholder")}
                  value={formData.lookingForOther}
                  onChange={(e) =>
                    updateField("lookingForOther", e.target.value)
                  }
                />
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                {t("social.form.personality")} <span className="text-rose-400">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {PERSONALITY_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                      formData.personalityType === type
                        ? "bg-[#a78bff] border-transparent text-[#0f0a14] shadow-lg shadow-black/30"
                        : "bg-[#1a131f] border-white/10 text-[#c8bfd1] hover:border-[#a78bff]/50 hover:bg-white/10"
                    }`}
                    onClick={() => updateField("personalityType", type)}
                  >
                    {optionLabel("personality", type)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                {t("social.form.communication")} <span className="text-rose-400">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {COMMUNICATION_STYLES.map((style) => (
                  <button
                    key={style}
                    type="button"
                    className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                      formData.communicationStyle === style
                        ? "bg-[#a78bff] border-transparent text-[#0f0a14] shadow-lg shadow-black/30"
                        : "bg-[#1a131f] border-white/10 text-[#c8bfd1] hover:border-[#a78bff]/50 hover:bg-white/10"
                    }`}
                    onClick={() => updateField("communicationStyle", style)}
                  >
                    {optionLabel("communication", style)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                {t("social.form.interests")} <span className="text-rose-400">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all duration-200 ${
                      formData.interests.includes(opt)
                        ? "bg-[#a78bff] border-transparent text-[#0f0a14] shadow-lg shadow-black/30"
                        : "bg-[#1a131f] border-white/10 text-[#c8bfd1] hover:border-[#a78bff]/50 hover:bg-white/10"
                    }`}
                    onClick={() => toggleArray("interests", opt)}
                  >
                    {optionLabel("interest", opt)}
                  </button>
                ))}
              </div>

              {formData.interests.includes("Other") && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                    {t("social.form.specify")} <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full bg-[#221a29]/80 text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 [color-scheme:dark] rounded-2xl px-5 py-3.5 text-base border-2 border-white/10 focus:border-[#a78bff] focus:outline-none focus:ring-4 focus:ring-[#a78bff]/20 transition-all duration-200"
                    placeholder={t("social.form.interestsOtherPlaceholder")}
                    value={formData.interestsOther}
                    onChange={(e) =>
                      updateField("interestsOther", e.target.value)
                    }
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <p className="text-[#c8bfd1] text-sm italic">
              {t("social.form.rateValues")}
            </p>

            {Object.keys(VALUE_LABELS).map((key) => (
              <div key={key} className="bg-[#221a29]/60 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-[#f7f3fa]">
                    {optionLabel("value", key)}
                  </span>
                  <span className="text-sm text-[#c8bfd1]">
                    {formData.values[key as keyof FormData["values"]]}/5
                  </span>
                </div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      type="button"
                      className={`flex-1 h-10 rounded-xl transition-all duration-200 ${
                        formData.values[key as keyof FormData["values"]] === num
                          ? "bg-[#a78bff] text-[#0f0a14] shadow-lg shadow-black/30"
                          : "bg-[#1a131f] border-2 border-white/10 text-[#c8bfd1] hover:border-[#a78bff]/50 hover:bg-white/10"
                      }`}
                      onClick={() =>
                        updateValue(key as keyof FormData["values"], num)
                      }
                    >
                      <span className="text-sm font-semibold">{num}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-[#f7f3fa] mb-2">
                {t("social.form.bio")}
              </label>
              <textarea
                className="w-full bg-[#221a29]/80 text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 [color-scheme:dark] rounded-2xl px-5 py-3.5 text-base border-2 border-white/10 focus:border-[#a78bff] focus:outline-none focus:ring-4 focus:ring-[#a78bff]/20 transition-all duration-200 h-24 resize-none placeholder:text-[#c8bfd1]"
                placeholder={t("social.form.bioPlaceholder")}
                value={formData.bio}
                onChange={(e) => updateField("bio", e.target.value)}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === 4;
  const progressWidth = (currentStep / 4) * 100;

  return (
    <div className="min-h-screen bg-[#0f0a14] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#a78bff]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#a78bff]/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#a78bff]/10 rounded-full blur-3xl"></div>

      <div className="w-full max-w-2xl relative z-10">
        {/* Header with brand */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="bg-[#a78bff] p-2 rounded-2xl shadow-lg shadow-black/30">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#d9ccff] to-[#a78bff] bg-clip-text text-transparent">
              Astria
            </h1>
          </div>
          <p className="text-sm text-[#c8bfd1] flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4 text-rose-400" />
            <span>ANIMO DIARIO — {t("social.form.tagline")}</span>
            <Sparkles className="w-4 h-4 text-rose-400" />
          </p>
        </div>

        <div className="bg-[#1a131f] backdrop-blur-xl rounded-3xl shadow-2xl shadow-black/30 border border-white/10 overflow-hidden">
          {/* Progress header */}
          <div className="px-8 pt-8 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {getStepIcon(currentStep)}
                  <span className="text-sm font-semibold text-rose-400">
                    {t("social.form.step", { step: currentStep })}
                  </span>
                  <span className="text-xs text-[#c8bfd1]">{t("social.form.of", { total: 4 })}</span>
                </div>
                <h2 className="text-2xl font-bold text-[#f7f3fa]">
                  {getStepTitle(currentStep)}
                </h2>
                <p className="text-sm text-[#c8bfd1]">
                  {getStepSubtitle(currentStep)}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#c8bfd1]">
                  {currentStep}/4
                </div>
              </div>
            </div>

            <div className="h-2 bg-[#221a29] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#a78bff] transition-all duration-500 ease-out rounded-full"
                style={{ width: `${progressWidth}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-8 pb-6 max-h-[55vh] overflow-y-auto scrollbar-thin scrollbar-thumb-rose-200 scrollbar-track-transparent">
            {renderPage()}
          </div>

          {/* Footer actions */}
          <div className="px-8 py-5 border-t border-white/10 bg-[#1a131f]/5 flex justify-between items-center">
            <button
              type="button"
              className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-medium transition-all duration-200 ${
                currentStep === 1
                  ? "bg-[#221a29] text-[#c8bfd1] cursor-not-allowed"
                  : "bg-[#221a29] text-[#c8bfd1] hover:bg-[#2a2133] hover:shadow-md"
              }`}
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="w-4 h-4" />
              {t("social.form.back")}
            </button>

            <button
              type="button"
              className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-medium text-[#0f0a14] transition-all duration-200 shadow-lg shadow-black/30 hover:brightness-110 hover:shadow-black/30 hover:scale-[1.02] active:scale-95 ${
                loading || isSubmitting
                  ? "bg-[#a78bff]/40 cursor-not-allowed"
                  : "bg-[#a78bff] hover:brightness-110"
              }`}
              onClick={handleNext}
              disabled={loading || isSubmitting}
            >
              {loading || isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t("social.form.saving")}
                </>
              ) : (
                <>
                  {isLastStep ? (
                    <>
                      <Check className="w-5 h-5" />
                      {t("social.form.complete")}
                    </>
                  ) : (
                    <>
                      {t("social.form.continue")}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-xs text-[#c8bfd1] flex items-center justify-center gap-2">
            <Feather className="w-3 h-3" />
            <span>
              "{t("social.form.quote")}"
            </span>
            <Feather className="w-3 h-3" />
          </p>
          <p className="text-xs text-[#c8bfd1] mt-1">{localizeBrand("— Astria", i18n.language)}</p>
        </div>
      </div>
    </div>
  );
};

export default CompatibilityForm;
